// src/services/pdfService.ts
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

interface CertificateData {
  name: string;
  company: string;
  number: string;
  date: string;
}

interface VoucherReportUser {
  user_id: string;
  user_email: string;
  user_name: string;
  company: string;
  registered_at: string;
  test_taken: boolean;
  test_score: number | null;
  test_passed: boolean | null;
  test_date: string | null;
  certificate_number: string | null;
  certificate_issued_at: string | null;
}

interface VoucherReportData {
  voucher: {
    code: string;
    partner: string;
    valid_from: string;
    valid_until: string;
  };
  statistics: {
    total_registrations: number;
    tests_taken: number;
    tests_passed: number;
    certificates_issued: number;
  };
  users: VoucherReportUser[];
  generated_at: string;
}

// Generate logo data URL from SVG
function generateLogoDataUrl(): Promise<string> {
  return new Promise((resolve, reject) => {
    const svgString = `
      <svg width="80" height="80" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(0,-952.36216)">
          <path 
            fill="#EE7F01"
            d="m 49.979083,957.36216 -25.142573,13.00244 0.02091,25.9726 25.397236,51.025 24.908919,-51.0579 -0.02099,-25.97231 -25.163495,-12.96983 z m 0.02091,3.96408 21.416994,11.05742 0,22.11483 -21.416994,11.05741 -21.416995,-11.05741 0,-22.11483 21.416995,-11.05742 z"
          />
          <path 
            fill="#EE7F01"
            d="m 46.216046,981.37189 15.844743,-10.99793 -8.276708,15.13629 -15.844743,10.99793 z"
          />
        </g>
      </svg>
    `;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = 80;
    canvas.height = 80;
    
    img.onload = () => {
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, 80, 80);
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } else {
        reject(new Error('Could not get canvas context'));
      }
    };
    
    img.onerror = (error) => {
      console.error('Error loading logo SVG:', error);
      reject(new Error('Failed to load logo'));
    };
    
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;
  });
}

// Generate QR code
function generateQRCode(certNumber: string): Promise<string> {
  const verificationUrl = `https://ki-kompetenznachweis.online/verify/${certNumber}`;
  return QRCode.toDataURL(verificationUrl, {
    width: 100,
    margin: 0,
    color: {
      dark: '#2574B5',
      light: '#FFFFFF'
    }
  });
}

// Main PDF generation function
export async function generateCertificatePDF(certData: CertificateData): Promise<jsPDF> {
  // Generate assets
  const [logoDataUrl, qrCodeUrl] = await Promise.all([
    generateLogoDataUrl().catch(() => null), // Graceful fallback
    generateQRCode(certData.number).catch(() => null) // Graceful fallback
  ]);

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = 297;
  const pageHeight = 210;
  const centerX = pageWidth / 2;

  // === HEADER SECTION ===
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'PNG', 20, 15, 22, 22);
    } catch (error) {
      console.warn('Could not add logo to PDF:', error);
    }
  }

  doc.setFontSize(13);
  doc.setTextColor(37, 116, 181);
  doc.text('KI-Navigatoren GmbH', 45, 23);

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('Ihr Partner für KI-Kompetenz', 45, 29);

  // === TITLE SECTION ===
  doc.setFontSize(36);
  doc.setTextColor(37, 116, 181);
  doc.text('Teilnahmezertifikat', centerX, 60, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(238, 127, 1);
  doc.text('KI-Kompetenznachweis gemäß EU AI Act', centerX, 70, { align: 'center' });

  doc.setDrawColor(238, 127, 1);
  doc.setLineWidth(0.5);
  doc.line(centerX - 55, 75, centerX + 55, 75);

  // === PARTICIPANT INFO ===
  doc.setFontSize(12);
  doc.setTextColor(80, 80, 80);
  doc.text('Hiermit wird bestätigt, dass', centerX, 85, { align: 'center' });

  doc.setFontSize(26);
  doc.setTextColor(37, 116, 181);
  doc.text(certData.name, centerX, 96, { align: 'center' });

  doc.setFontSize(12);
  doc.setTextColor(80, 80, 80);
  doc.text('von', centerX, 106, { align: 'center' });

  doc.setFontSize(18);
  doc.setTextColor(37, 116, 181);
  doc.text(certData.company, centerX, 114, { align: 'center' });

  // === CERTIFICATION STATEMENT ===
  doc.setFontSize(13);
  doc.setTextColor(40, 40, 40);
  const statementLines = [
    'erfolgreich die Schulung zum EU AI Act absolviert und den',
    'Kompetenznachweis durch Teilnahme am Abschlusstest erbracht hat.',
    '',
    'Ziel war der Aufbau grundlegender Kompetenzen im Umgang mit',
    'Künstlicher Intelligenz im Unternehmen.'
  ];

  let textY = 130;
  statementLines.forEach((line, index) => {
    doc.text(line, centerX, textY + index * 5, { align: 'center' });
  });

  // === DOCUMENT BORDER ===
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

  // === QR CODE SECTION ===
  const footerTopY = pageHeight - 30;
  if (qrCodeUrl) {
    try {
      doc.addImage(qrCodeUrl, 'PNG', 25, footerTopY - 14, 25, 25);
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('Zertifikat verifizieren:', 25, footerTopY + 15);
      doc.text('ki-kompetenznachweis.online/verify/' + certData.number, 25, footerTopY + 18);
    } catch (error) {
      console.warn('Could not add QR code to PDF:', error);
      // Add verification text without QR code
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Zertifikat verifizieren unter:', 25, footerTopY + 5);
      doc.text('ki-kompetenznachweis.online/verify/' + certData.number, 25, footerTopY + 10);
    }
  }

  // Certificate Number
  doc.setFontSize(10);
  doc.setTextColor(37, 116, 181);
  doc.text('Zertifikatsnummer:', 60, footerTopY - 2);
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(certData.number, 60, footerTopY + 3);

  // Issuer
  doc.setFontSize(10);
  doc.setTextColor(37, 116, 181);
  doc.text('Aussteller:', centerX, footerTopY - 2, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text('KI-Navigatoren GmbH', centerX, footerTopY + 3, { align: 'center' });

  // Issue Date
  doc.setFontSize(10);
  doc.setTextColor(37, 116, 181);
  doc.text('Ausstellungsdatum:', pageWidth - 60, footerTopY - 2, { align: 'right' });
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(certData.date, pageWidth - 60, footerTopY + 3, { align: 'right' });

  return doc;
}

export async function generateVoucherReportPDF(reportData: VoucherReportData): Promise<jsPDF> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = 210;
  const marginLeft = 15;
  const marginRight = 15;
  const contentWidth = pageWidth - marginLeft - marginRight;
  let yPosition = 20;

  const logoDataUrl = await generateLogoDataUrl().catch(() => null);

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'PNG', marginLeft, yPosition, 20, 20);
    } catch (error) {
      console.warn('Could not add logo to PDF:', error);
    }
  }

  doc.setFontSize(20);
  doc.setTextColor(37, 116, 181);
  doc.text('Voucher Report', marginLeft + 25, yPosition + 10);

  yPosition += 30;

  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text(`Voucher-Code: ${reportData.voucher.code}`, marginLeft, yPosition);
  yPosition += 7;
  doc.text(`Partner: ${reportData.voucher.partner}`, marginLeft, yPosition);
  yPosition += 7;
  doc.text(`Gültigkeit: ${new Date(reportData.voucher.valid_from).toLocaleDateString('de-DE')} - ${new Date(reportData.voucher.valid_until).toLocaleDateString('de-DE')}`, marginLeft, yPosition);
  yPosition += 7;
  doc.text(`Erstellt am: ${new Date(reportData.generated_at).toLocaleDateString('de-DE')} um ${new Date(reportData.generated_at).toLocaleTimeString('de-DE')}`, marginLeft, yPosition);

  yPosition += 15;

  doc.setDrawColor(238, 127, 1);
  doc.setLineWidth(0.5);
  doc.line(marginLeft, yPosition, pageWidth - marginRight, yPosition);
  yPosition += 10;

  doc.setFontSize(14);
  doc.setTextColor(37, 116, 181);
  doc.text('Statistik', marginLeft, yPosition);
  yPosition += 10;

  doc.setFontSize(11);
  doc.setTextColor(40, 40, 40);
  const stats = reportData.statistics;
  doc.text(`Gesamte Registrierungen: ${stats.total_registrations}`, marginLeft, yPosition);
  yPosition += 6;
  doc.text(`Tests absolviert: ${stats.tests_taken}`, marginLeft, yPosition);
  yPosition += 6;
  doc.text(`Tests bestanden: ${stats.tests_passed}`, marginLeft, yPosition);
  yPosition += 6;
  doc.text(`Zertifikate ausgestellt: ${stats.certificates_issued}`, marginLeft, yPosition);

  yPosition += 15;

  doc.setDrawColor(238, 127, 1);
  doc.line(marginLeft, yPosition, pageWidth - marginRight, yPosition);
  yPosition += 10;

  doc.setFontSize(14);
  doc.setTextColor(37, 116, 181);
  doc.text('Teilnehmer-Übersicht', marginLeft, yPosition);
  yPosition += 10;

  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);

  const tableHeaders = ['Name', 'Unternehmen', 'Test', 'Score', 'Zertifikat'];
  const colWidths = [40, 45, 25, 15, 50];
  const startX = marginLeft;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFillColor(37, 116, 181);
  doc.rect(startX, yPosition - 5, contentWidth, 7, 'F');

  let xPos = startX + 2;
  tableHeaders.forEach((header, index) => {
    doc.text(header, xPos, yPosition);
    xPos += colWidths[index];
  });

  yPosition += 5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);

  for (let i = 0; i < reportData.users.length; i++) {
    const user = reportData.users[i];

    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }

    if (i % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(startX, yPosition - 4, contentWidth, 6, 'F');
    }

    xPos = startX + 2;

    doc.text(user.user_name || 'N/A', xPos, yPosition, { maxWidth: colWidths[0] - 3 });
    xPos += colWidths[0];

    doc.text(user.company || 'N/A', xPos, yPosition, { maxWidth: colWidths[1] - 3 });
    xPos += colWidths[1];

    const testStatus = user.test_passed === true ? 'Bestanden' : user.test_passed === false ? 'Nicht bestanden' : 'Offen';
    doc.text(testStatus, xPos, yPosition);
    xPos += colWidths[2];

    doc.text(user.test_score !== null ? `${user.test_score}%` : '-', xPos, yPosition);
    xPos += colWidths[3];

    doc.text(user.certificate_number || '-', xPos, yPosition, { maxWidth: colWidths[4] - 3 });

    yPosition += 6;
  }

  yPosition += 10;

  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('KI-Navigatoren GmbH', marginLeft, pageWidth - 10);
  doc.text(`Seite ${doc.getCurrentPageInfo().pageNumber}`, pageWidth - marginRight - 20, pageWidth - 10);

  return doc;
}

export async function generateCollectiveCertificatePDF(users: VoucherReportUser[]): Promise<jsPDF | null> {
  const certificateUsers = users.filter(u => u.certificate_number);

  if (certificateUsers.length === 0) {
    return null;
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  for (let i = 0; i < certificateUsers.length; i++) {
    const user = certificateUsers[i];

    if (i > 0) {
      doc.addPage('a4', 'landscape');
    }

    const certData: CertificateData = {
      name: user.user_name,
      company: user.company,
      number: user.certificate_number!,
      date: new Date(user.certificate_issued_at!).toLocaleDateString('de-DE')
    };

    // Generate QR code and logo for this certificate
    const [logoDataUrl, qrCodeUrl] = await Promise.all([
      generateLogoDataUrl().catch(() => null),
      generateQRCode(certData.number).catch(() => null)
    ]);

    const pageWidth = 297;
    const pageHeight = 210;
    const centerX = pageWidth / 2;

    // === HEADER SECTION ===
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, 'PNG', 20, 15, 22, 22);
      } catch (error) {
        console.warn('Could not add logo to PDF:', error);
      }
    }

    doc.setFontSize(13);
    doc.setTextColor(37, 116, 181);
    doc.text('KI-Navigatoren GmbH', 45, 23);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Ihr Partner für KI-Kompetenz', 45, 29);

    // === TITLE SECTION ===
    doc.setFontSize(36);
    doc.setTextColor(37, 116, 181);
    doc.text('Teilnahmezertifikat', centerX, 60, { align: 'center' });

    doc.setFontSize(14);
    doc.setTextColor(238, 127, 1);
    doc.text('KI-Kompetenznachweis gemäß EU AI Act', centerX, 70, { align: 'center' });

    doc.setDrawColor(238, 127, 1);
    doc.setLineWidth(0.5);
    doc.line(centerX - 55, 75, centerX + 55, 75);

    // === PARTICIPANT INFO ===
    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.text('Hiermit wird bestätigt, dass', centerX, 85, { align: 'center' });

    doc.setFontSize(26);
    doc.setTextColor(37, 116, 181);
    doc.text(certData.name, centerX, 96, { align: 'center' });

    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.text('von', centerX, 106, { align: 'center' });

    doc.setFontSize(18);
    doc.setTextColor(37, 116, 181);
    doc.text(certData.company, centerX, 114, { align: 'center' });

    // === CERTIFICATION STATEMENT ===
    doc.setFontSize(13);
    doc.setTextColor(40, 40, 40);
    const statementLines = [
      'erfolgreich die Schulung zum EU AI Act absolviert und den',
      'Kompetenznachweis durch Teilnahme am Abschlusstest erbracht hat.',
      '',
      'Ziel war der Aufbau grundlegender Kompetenzen im Umgang mit',
      'Künstlicher Intelligenz im Unternehmen.'
    ];

    let textY = 130;
    statementLines.forEach((line, index) => {
      doc.text(line, centerX, textY + index * 5, { align: 'center' });
    });

    // === DOCUMENT BORDER ===
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

    // === QR CODE SECTION ===
    const footerTopY = pageHeight - 30;
    if (qrCodeUrl) {
      try {
        doc.addImage(qrCodeUrl, 'PNG', 25, footerTopY - 14, 25, 25);
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text('Zertifikat verifizieren:', 25, footerTopY + 15);
        doc.text('ki-kompetenznachweis.online/verify/' + certData.number, 25, footerTopY + 18);
      } catch (error) {
        console.warn('Could not add QR code to PDF:', error);
        // Add verification text without QR code
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('Zertifikat verifizieren unter:', 25, footerTopY + 5);
        doc.text('ki-kompetenznachweis.online/verify/' + certData.number, 25, footerTopY + 10);
      }
    }

    // Certificate Number
    doc.setFontSize(10);
    doc.setTextColor(37, 116, 181);
    doc.text('Zertifikatsnummer:', 60, footerTopY - 2);
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(certData.number, 60, footerTopY + 3);

    // Issuer
    doc.setFontSize(10);
    doc.setTextColor(37, 116, 181);
    doc.text('Aussteller:', centerX, footerTopY - 2, { align: 'center' });
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text('KI-Navigatoren GmbH', centerX, footerTopY + 3, { align: 'center' });

    // Issue Date
    doc.setFontSize(10);
    doc.setTextColor(37, 116, 181);
    doc.text('Ausstellungsdatum:', pageWidth - 60, footerTopY - 2, { align: 'right' });
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(certData.date, pageWidth - 60, footerTopY + 3, { align: 'right' });
  }

  return doc;
}